import moment from 'moment-timezone'

// Formats used in Axilon
const dateFormat = 'MMM D, YYYY'
const DateTimeFormat = 'MMM D, YYYY HH:mm:ss'
const compactDateTime = 'YYYY MMM D, h:mm A'

const defaultTimeZone = 'UTC'

interface FormatOptions {
  format: string
  defaultValue?: string
  timeZone?: string
  local?: boolean
}

type RestrictedFormatOptions = Omit<FormatOptions, 'format'>

// `null` is accepted on every formatter below so API responses with
// nullable date fields (`createdAt: string | null`, etc.) can be passed
// directly without callers having to coalesce to `undefined` first. The
// falsy short-circuit at the top of `customFormatDate` already handles
// `null`/`undefined`/`''` identically — this is purely a type widening.
type DateLike = string | number | Date | null | undefined

export const customFormatDate = (date?: DateLike, options?: FormatOptions) => {
  if (!date) return options?.defaultValue ?? ''

  let momentDate = moment.tz(date, options?.timeZone ?? defaultTimeZone)

  if (options?.local ?? true) {
    momentDate = momentDate.local()
  }

  return momentDate.format(options?.format ?? dateFormat)
}

export const formatDate = (date?: DateLike, options?: RestrictedFormatOptions) =>
  customFormatDate(date, { format: dateFormat, ...options })

export const formatDateTime = (date?: DateLike, options?: RestrictedFormatOptions) =>
  customFormatDate(date, { format: DateTimeFormat, ...options })

export const formatCompactDateTime = (date?: DateLike, options?: RestrictedFormatOptions) =>
  customFormatDate(date, { format: compactDateTime, ...options })

/**
 * Parse an API date-time string as UTC and return ms since epoch.
 * Use this for relative time (e.g. "X minutes ago") so server UTC times
 * are not misinterpreted as local time when the string has no timezone suffix.
 */
export const parseDateAsUtc = (date?: DateLike): number | null => {
  if (date === undefined || date === null) return null
  const m = moment.tz(date, defaultTimeZone)
  return m.isValid() ? m.valueOf() : null
}
